import React, { createContext, useContext, useState, useEffect } from 'react';
import ApiService from '../util/apiService';

const AuthContext = createContext(undefined);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    // Check if user is already logged in on app start
    checkAuthStatus();
  }, []);

  const checkAuthStatus = async () => {
    try {
      setIsLoading(true);
      const isAuth = await ApiService.isAuthenticated();
      if (isAuth) {
        const currentUser = await ApiService.getCurrentUser();
        if (currentUser) {
          setUser(currentUser);
          // Optionally refresh user data from server
          try {
            const freshUserData = await ApiService.getUserProfile();
            setUser(freshUserData);
          } catch (error) {
            console.log('Failed to refresh user data:', error);
            // Keep local user data if server request fails
          }
        }
      }
    } catch (error) {
      console.error('Error checking auth status:', error);
      await ApiService.clearAuthData();
    } finally {
      setIsLoading(false);
    }
  };

  const login = async (credentials) => {
    try {
      setIsLoading(true);
      const response = await ApiService.login(credentials);
      if (response.success && response.user) {
        setUser(response.user);
      }
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const register = async (userData) => {
    try {
      setIsLoading(true);
      const response = await ApiService.register(userData);
      if (response.success && response.user) {
        setUser(response.user);
      }
    } catch (error) {
      throw error;
    } finally {
      setIsLoading(false);
    }
  };

  const logout = async () => {
    try {
      setIsLoading(true);
      await ApiService.logout();
      setUser(null);
    } catch (error) {
      console.error('Logout error:', error);
      // Clear local data even if API call fails
      setUser(null);
      await ApiService.clearAuthData();
    } finally {
      setIsLoading(false);
    }
  };

  const updateProfile = async (profileData) => {
    try {
      await ApiService.updateUserProfile(profileData);
      // Refresh user data
      await refreshUser();
    } catch (error) {
      throw error;
    }
  };

  const updateBio = async (bio) => {
    try {
      await ApiService.updateUserBio(bio);
      // Update local user state
      if (user) {
        setUser({ ...user, bio });
      }
    } catch (error) {
      throw error;
    }
  };

  const refreshUser = async () => {
    try {
      const freshUserData = await ApiService.getUserProfile();
      setUser(freshUserData);
    } catch (error) {
      console.error('Failed to refresh user data:', error);
      throw error;
    }
  };

  const isAuthenticated = !!user;

  const value = {
    user,
    isAuthenticated,
    isLoading,
    login,
    register,
    logout,
    updateProfile,
    updateBio,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
